import { dev, handled } from "@phosphor/utils";
import { type Atom, atom } from "jotai";
import { CActions, UKeydownRootHandler } from "#scripts/keyboard-demo/plugins/CActions.ts";
import { CFocusable, UCurrentFocus } from "#scripts/keyboard-demo/plugins/CFocusable.ts";
import { CParent } from "#scripts/keyboard-demo/plugins/CParent.ts";
import { World, type WorldState, uid } from "#scripts/vmc/WorldState.ts";

// Guided by @docs/WorldState.md
// Component for card label/title
export class CLabel extends World.Component("label")<
  CLabel,
  {
    titleAtom: Atom<string>;
    helpTextAtom: Atom<string>;
  }
>() {}

// Guided by @docs/WorldState.md
// Component for card color aesthetics
export class CColor extends World.Component("color")<
  CColor,
  {
    borderColorAtom: Atom<string>;
  }
>() {}

// Guided by @docs/WorldState.md
// Component for managing a list of card UIDs
export class CCardList extends World.Component("card-list")<
  CCardList,
  {
    cardUIDsAtom: Atom<World.UID[]>;
  }
>() {}

// Root entity that manages the list of cards
const RootEntity = World.Entity("keyboard-demo-root", {
  components: [CCardList],
})();

// Simple card entity with focusable + actions + aesthetics
const CardEntity = World.Entity("card", {
  components: [CFocusable, CActions, CParent, CLabel, CColor],
})();

// Card-specific actions
const CardActions = CActions.defineActions(
  "card-actions",
  { source: dev`Card actions`.ctx({ loc: "phosphor-blog/scripts/keyboard-demo/createKeyboardDemo.ts:49:13" }), sectionName: "Card Actions" },
  {
    delete: {
      label: "Delete Card",
      defaultKeybinding: "X",
      description: "Remove this card",
    },
    duplicate: {
      label: "Duplicate Card",
      defaultKeybinding: "D",
      description: "Create a copy of this card",
    },
    edit: {
      label: "Edit Card",
      defaultKeybinding: "E",
      description: "Edit card content",
    },
  },
);

// Navigation actions available on all cards
const NavigationActions = CActions.defineActions(
  "navigation-actions",
  { source: dev`Navigation actions`.ctx({ loc: "phosphor-blog/scripts/keyboard-demo/createKeyboardDemo.ts:72:13" }), sectionName: "Navigation" },
  {
    moveUp: {
      label: "Move Up",
      defaultKeybinding: "ArrowUp" as const,
      description: "Focus previous card",
    },
    moveDown: {
      label: "Move Down",
      defaultKeybinding: "ArrowDown" as const,
      description: "Focus next card",
    },
  },
);

// Helper function to create a card entity
function createCardEntity(
  world: WorldState,
  rootUID: World.UID,
  key: string,
  title: string,
  color: string,
  index: number,
  cardUIDs: World.UID[],
): World.UID {
  const focusState = world.getUniqueOrThrow(UCurrentFocus);
  const cardUID = uid(null, null, key);

  const onDelete = () => {
    console.log(`Delete ${title}`);
    alert(`Deleted ${title}!`);
  };

  const onDuplicate = () => {
    console.log(`Duplicate ${title}`);
    alert(`Duplicated ${title}!`);
  };

  const onEdit = () => {
    console.log(`Edit ${title}`);
    alert(`Editing ${title}!`);
  };

  const onNavigate = (direction: "up" | "down") => {
    const currentIndex = cardUIDs.indexOf(cardUID);
    let nextIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (nextIndex < 0) nextIndex = cardUIDs.length - 1;
    if (nextIndex >= cardUIDs.length) nextIndex = 0;
    const nextUID = cardUIDs[nextIndex];
    if (nextUID) {
      world.getComponentMaybe(nextUID, CFocusable)?.handler(dev`Card focused`.ctx({ loc: "phosphor-blog/scripts/keyboard-demo/createKeyboardDemo.ts:122:61" }), direction);
    }
  };

  const hasDirectFocusAtom = atom((get) => get(focusState.activeFocusAtom) === cardUID);

  world.addEntity(cardUID, CardEntity, {
    focusable: CFocusable.of({
      hasDirectFocusAtom,
      handler: (_direction) => {
        world.store.set(focusState.activeFocusAtom, cardUID);
        return handled`Card focused`.ctx({ loc: "phosphor-blog/scripts/keyboard-demo/createKeyboardDemo.ts:133:16" });
      },
    }),
    actions: CActions.merge(
      {
        bindingSource: dev`Card ${index} actions`.ctx({ loc: "phosphor-blog/scripts/keyboard-demo/createKeyboardDemo.ts:138:24" }),
        registryKey: CardActions,
        bindingsAtom: atom({
          delete: (_reason, _event) => (onDelete(), handled`delete`.ctx({ loc: "phosphor-blog/scripts/keyboard-demo/createKeyboardDemo.ts:141:53" })),
          duplicate: (_reason, _event) => (onDuplicate(), handled`duplicate`.ctx({ loc: "phosphor-blog/scripts/keyboard-demo/createKeyboardDemo.ts:142:59" })),
          edit: (_reason, _event) => (onEdit(), handled`edit`.ctx({ loc: "phosphor-blog/scripts/keyboard-demo/createKeyboardDemo.ts:143:49" })),
        }),
      },
      {
        bindingSource: dev`Card ${index} navigation`.ctx({ loc: "phosphor-blog/scripts/keyboard-demo/createKeyboardDemo.ts:147:24" }),
        registryKey: NavigationActions,
        bindingsAtom: atom({
          moveUp: (_reason, _event) => (onNavigate("up"), handled`moveUp`.ctx({ loc: "phosphor-blog/scripts/keyboard-demo/createKeyboardDemo.ts:150:59" })),
          moveDown: (_reason, _event) => (onNavigate("down"), handled`moveDown`.ctx({ loc: "phosphor-blog/scripts/keyboard-demo/createKeyboardDemo.ts:151:63" })),
        }),
      },
    ),
    parent: CParent.of({
      uidAtom: atom(rootUID),
    }),
    label: CLabel.of({
      titleAtom: atom(title),
      helpTextAtom: atom((get) =>
        get(hasDirectFocusAtom) ? "↑/↓: Navigate · X: Delete · D: Duplicate · E: Edit" : "Click to focus",
      ),
    }),
    color: CColor.of({
      borderColorAtom: atom(color),
    }),
  });

  return cardUID;
}

export function createKeyboardDemo(world: WorldState) {
  const rootUID = uid(null, null, "keyboard-demo-root");

  const cardConfigs = [
    { key: "card-1", title: "Todo Item", color: "#3b82f6" },
    { key: "card-2", title: "Note", color: "#10b981" },
    { key: "card-3", title: "Task", color: "#f59e0b" },
  ];

  // Create card entities and collect UIDs (pass mutable array to be filled)
  const cardUIDs: World.UID[] = [];
  cardConfigs.forEach((config, index) => {
    const cardUID = createCardEntity(world, rootUID, config.key, config.title, config.color, index, cardUIDs);
    cardUIDs.push(cardUID);
  });

  // Create root entity with card list
  const cardUIDsAtom = atom<World.UID[]>(cardUIDs);
  world.addEntity(rootUID, RootEntity, {
    "card-list": CCardList.of({
      cardUIDsAtom,
    }),
  });

  // Set root UID for fallback keyboard handling
  const keydownUnique = world.getUniqueOrThrow(UKeydownRootHandler);
  world.store.set(keydownUnique.rootUIDAtom, rootUID);

  return { rootUID };
}
